import { useQuery } from '@apollo/client';
import { Navigate, useNavigate } from 'react-router-dom';
import { Container, Header, Icon, Label, Loader, Message, Table } from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import { POLL_STATUS_LABEL } from '../../../utils/poll';
import { GET_POLLS } from './queries';

/** Content → Polls: every poll this organisation has asked, newest first. Content reviewers only. */
export function PollsView() {
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const navigate = useNavigate();
    const { data, loading, error } = useQuery(GET_POLLS, { fetchPolicy: 'cache-and-network', skip: !isContentReviewer });

    if (!meLoading && !isContentReviewer) return <Navigate to={PATHS.CLASSES} replace />;

    const polls: any[] = data?.polls ?? [];

    return (
        <ResponsiveContainer>
            <Container style={{ padding: '2em 0' }}>
                <Header as="h1">
                    <Icon name="chart bar" />
                    <Header.Content>
                        Polls
                        <Header.Subheader>
                            Every poll your organisation has asked. Create one from Content → Create post.
                        </Header.Subheader>
                    </Header.Content>
                </Header>

                {(loading || meLoading) && polls.length === 0 && <Loader active inline="centered" />}
                {error && <Message error content={error.message} />}
                {!loading && !error && polls.length === 0 && (
                    <Message info content="No polls yet. Choose “Poll” as the post type in Content → Create post." />
                )}

                {polls.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <Table selectable unstackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>Question</Table.HeaderCell>
                                    <Table.HeaderCell>Status</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Responses</Table.HeaderCell>
                                    <Table.HeaderCell>Asked</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {polls.map((poll) => {
                                    const status = POLL_STATUS_LABEL[poll.status] ?? { text: poll.status, color: 'grey' as const };
                                    const asked = parseApiDate(poll.publishAt ?? poll.createdAt);
                                    return (
                                        <Table.Row
                                            key={poll.postId}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate(PATHS.POLL_RESULTS.replace(':postId', String(poll.postId)))}
                                        >
                                            <Table.Cell>
                                                <strong>{poll.question || 'Untitled poll'}</strong>
                                                <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.6)' }}>
                                                    {poll.allowMultiple ? 'Several answers' : 'One answer'}
                                                    {poll.allowOther ? ' · “Something else” on' : ''}
                                                    {poll.streamName ? ` · ${poll.streamName}` : ''}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Label size="small" color={status.color} basic={status.color === 'grey'}>{status.text}</Label>
                                            </Table.Cell>
                                            <Table.Cell textAlign="right">{poll.respondentCount}</Table.Cell>
                                            <Table.Cell>{asked ? asked.toLocaleDateString() : '—'}</Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </div>
                )}
            </Container>
        </ResponsiveContainer>
    );
}
